import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Template } from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { APP_BASE_HREF, appDispatch } from "../App";
import {
  startWithOrganisationAndModel,
  startWithSimulation,
  startWithTemplate,
} from "../state/appState";

import ChooseSimulationTemplateScreen from "./ChooseSimulationTemplateScreen";

export const PossibleStartWithModelAndOrganisation = () => {
  // What we do depends on the model's number of setting templates
  // 0 - start in old way (QuickstartWithModelAndOrganisation)
  // 1 - start with that template setting using QuickstartWithSimulationTemplateAndOrganisation
  // 2 or more - show a screen that lets the user choose, then 1

  const { modelId, organisationId } = useParams<{ modelId: string; organisationId: string }>();

  const [simulationTemplates, setSimulationTemplates] = useState<Template[] | null>(null);
  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    if (modelId === undefined) return;
    const fetchSimulationSettings = async () => {
      const api = getV3Api();
      const response = await api.simulationTemplatesList({
        simulationThreedimodelId: Number(modelId),
        limit: 10, // We don't need more here
      });

      if (response.results) {
        setSimulationTemplates(response.results);
        setCount(response.count);
      }
    };
    fetchSimulationSettings();
  }, [modelId]);

  if (simulationTemplates === null) return null;
  if (modelId === undefined || organisationId === undefined) return null;

  if (simulationTemplates.length === 0) {
    // Start the old way
    return <QuickstartWithModelAndOrganisation />;
  } else if (simulationTemplates.length === 1) {
    const templateId = simulationTemplates[0].id;
    if (templateId === undefined) return null;
    // Start with template
    return (
      <QuickstartWithSimulationTemplateAndOrganisationComponent
        modelId={Number(modelId)}
        organisationId={organisationId}
        simulationTemplateId={templateId}
      />
    );
  } else {
    // Show selection screen
    return (
      <ChooseSimulationTemplateScreen
        modelId={Number(modelId)}
        organisationId={organisationId}
        simulationTemplates={simulationTemplates}
        count={count}
      />
    );
  }
};

export const QuickstartWithModelAndOrganisation = () => {
  const { modelId, organisationId } = useParams<{ modelId: string; organisationId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (modelId === undefined || organisationId === undefined) return;
      const v3api = getV3Api();
      const threediModel = await v3api.threedimodelsRead({
        id: Number(modelId),
      });
      const organisation = await v3api.organisationsRead({
        uniqueId: organisationId,
      });

      if (threediModel && organisation) {
        appDispatch(startWithOrganisationAndModel(organisation, threediModel));
      }
      navigate(`${APP_BASE_HREF}/`);
    })();
  });

  return null;
};

export const QuickstartWithSimulationTemplateAndOrganisation = () => {
  const { modelId, organisationId, simulationTemplateId } = useParams<{
    modelId: string;
    organisationId: string;
    simulationTemplateId: string;
  }>();

  return (
    <QuickstartWithSimulationTemplateAndOrganisationComponent
      modelId={modelId === undefined ? 0 : Number(modelId)}
      organisationId={organisationId ?? ""}
      simulationTemplateId={simulationTemplateId === undefined ? 0 : Number(simulationTemplateId)}
    />
  );
};

const QuickstartWithSimulationTemplateAndOrganisationComponent: FC<{
  modelId: number;
  organisationId: string;
  simulationTemplateId: number;
}> = ({ modelId, organisationId, simulationTemplateId }) => {
  const navigate = useNavigate();

  useEffect(() => {
    startWithTemplate(modelId, organisationId, simulationTemplateId, appDispatch).then(() =>
      navigate(`${APP_BASE_HREF}/`)
    );
  }, [modelId, organisationId, simulationTemplateId, navigate]);

  return null;
};

export const QuickstartWithSimulation = () => {
  const { simulationId } = useParams<{ simulationId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const v3api = getV3Api();
      const simulation = await v3api.simulationsRead({
        id: simulationId === undefined ? 0 : Number(simulationId),
      });

      if (simulation) {
        appDispatch(startWithSimulation(simulation));
      }
      navigate(`${APP_BASE_HREF}/`);
    })();
  });

  return null;
};
